import React from 'react';
import { ChessPiece } from './ChessPiece';
import type { ChessBoard as ChessBoardType, ChessPosition, ChessMove } from '../../../types/chess';

interface ChessBoardProps {
  board: ChessBoardType;
  selectedPiece: ChessPosition | null;
  validMoves: ChessPosition[];
  lastMove: ChessMove | null;
  onSelectPiece: (position: ChessPosition | null) => void;
  onMove: (from: ChessPosition, to: ChessPosition) => void;
  disabled?: boolean;
}

export function ChessBoard({
  board,
  selectedPiece,
  validMoves,
  lastMove,
  onSelectPiece,
  onMove,
  disabled
}: ChessBoardProps) {
  const handleSquareClick = (row: number, col: number) => {
    if (disabled) return;

    const piece = board[row][col];
    
    // If a piece is already selected, try to move it
    if (selectedPiece) {
      if (validMoves.some(move => move.row === row && move.col === col)) {
        onMove(selectedPiece, { row, col });
      }
      onSelectPiece(null);
      return;
    }

    // Select a piece if it's the player's
    if (piece?.color === 'white') {
      onSelectPiece({ row, col });
    }
  };

  return (
    <div className="bg-gray-800 p-4 rounded-lg">
      <div className="grid grid-cols-8 gap-1">
        {board.map((row, rowIndex) => (
          row.map((piece, colIndex) => {
            const isSelected = selectedPiece?.row === rowIndex && selectedPiece?.col === colIndex;
            const isValidMove = validMoves.some(move => move.row === rowIndex && move.col === colIndex);
            const isLastMove = lastMove && (
              (lastMove.from.row === rowIndex && lastMove.from.col === colIndex) ||
              (lastMove.to.row === rowIndex && lastMove.to.col === colIndex)
            );

            return (
              <button
                key={`${rowIndex}-${colIndex}`}
                onClick={() => handleSquareClick(rowIndex, colIndex)}
                disabled={disabled}
                className={`
                  w-12 h-12 flex items-center justify-center
                  ${(rowIndex + colIndex) % 2 === 0 ? 'bg-amber-800' : 'bg-amber-700'}
                  ${isSelected ? 'ring-2 ring-yellow-400' : ''}
                  ${isValidMove ? 'ring-2 ring-green-400' : ''}
                  ${isLastMove ? 'ring-2 ring-blue-400' : ''}
                  transition-colors
                `}
              >
                {piece && <ChessPiece piece={piece} />}
              </button>
            );
          })
        ))}
      </div>
    </div>
  );
}